import {
  Box,
  Card,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import Iconify from 'src/components/iconify';
import { mapSpecializationNameIdToString } from 'src/utils/mapSpecializationNameIdToString';
import { GetProfessionalConfigDtoEnabledCategoriesEnum } from 'woofwoof-api';
import { translateKey } from 'src/locales/translate';

// Service type from backend - matching SpecializationService schema
type Service = {
  _id?: string;
  id?: string;
  name: string;
  description?: string;
  category: string;
  visitType?: string;
  specializationId?: any; // Populated specialization object
  defaultPrice: number;
  defaultDuration: number;
  isActive: boolean;
};

type ServiceConfigData = {
  serviceId: string;
  customDuration?: number;
  notes?: string;
  isActive: boolean;
};

type Props = {
  categoryId: string;
  categoryName: string;
  visitType?: string;
  services: Service[];
  serviceConfigs: ServiceConfigData[];
  onServiceConfigsChange: (configs: ServiceConfigData[]) => void;
  disabled?: boolean;
};

export default function ServiceConfigSection({
  categoryId,
  categoryName,
  visitType,
  services,
  serviceConfigs,
  onServiceConfigsChange,
  disabled = false,
}: Props) {
  const [expandedServices, setExpandedServices] = useState<Set<string>>(
    new Set(),
  );

  const categoryServices = services.filter(
    (service: any) =>
      service.category === categoryId &&
      (!visitType || service.visitType === visitType),
  );

  const toggleService = (serviceId: string) => {
    const existing = serviceConfigs.find((sc) => sc.serviceId === serviceId);

    if (existing) {
      // Remove service
      onServiceConfigsChange(
        serviceConfigs.filter((sc) => sc.serviceId !== serviceId),
      );
    } else {
      // Add service with defaults
      onServiceConfigsChange([
        ...serviceConfigs,
        {
          serviceId,
          customDuration: undefined,
          notes: '',
          isActive: true,
        },
      ]);
    }
  };

  const updateServiceConfig = (
    serviceId: string,
    updates: Partial<ServiceConfigData>,
  ) => {
    onServiceConfigsChange(
      serviceConfigs.map((sc) =>
        sc.serviceId === serviceId ? { ...sc, ...updates } : sc,
      ),
    );
  };

  const toggleExpand = (serviceId: string) => {
    setExpandedServices((prev) => {
      const next = new Set(prev);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
  };

  if (categoryServices.length === 0) {
    return null;
  }

  return (
    <Card sx={{ p: 2, bgcolor: 'background.neutral' }}>
      <Typography variant="subtitle2" gutterBottom>{translateKey("hardcoded.uslugi_dla_kategorii")}{categoryName}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 2, display: 'block' }}
      >{translateKey("hardcoded.wybierz_uslugi_ktore_oferujesz_w_tej_kategorii")}</Typography>

      <Stack spacing={2}>
        {(() => {
          // Group services by specialization
          const servicesBySpecialization = categoryServices.reduce(
            (acc: any, service: any) => {
              const specId =
                service.specializationId?._id ||
                service.specializationId?.id ||
                service.specializationId ||
                'unknown';
              if (!acc[specId]) {
                acc[specId] = {
                  specializationName: service.specializationId
                    ? mapSpecializationNameIdToString(service.specializationId)
                    : 'Nieznana specjalizacja',
                  services: [],
                };
              }
              acc[specId].services.push(service);
              return acc;
            },
            {},
          );

          return Object.entries(servicesBySpecialization).map(
            ([specId, data]: [string, any]) => (
              <Box key={specId}>
                {/* Specialization header */}
                <Typography
                  variant="caption"
                  fontWeight="medium"
                  color="text.secondary"
                  sx={{ mb: 1, display: 'block' }}
                >
                  {data.specializationName}
                </Typography>

                {/* Services under this specialization */}
                <Stack spacing={1}>
                  {data.services.map((service: any) => {
                    const serviceId = service._id || service.id;
                    const isEnabled = serviceConfigs.some(
                      (sc) => sc.serviceId === serviceId,
                    );
                    const config = serviceConfigs.find(
                      (sc) => sc.serviceId === serviceId,
                    );
                    const isExpanded = expandedServices.has(serviceId);

                    return (
                      <Box key={serviceId}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1.5,
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            border: 1,
                            borderColor: isEnabled ? 'primary.main' : 'divider',
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isEnabled}
                                onChange={() => toggleService(serviceId)}
                                disabled={disabled}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2">
                                  {service.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {service.defaultDuration} min
                                  {service.category !==
                                    GetProfessionalConfigDtoEnabledCategoriesEnum.Veterinarian &&
                                    translateKey("hardcoded.priceBullet", { price: service.defaultPrice })}
                                </Typography>
                              </Box>
                            }
                            sx={{ flexGrow: 1, m: 0 }}
                          />

                          {isEnabled && (
                            <>
                              {config?.customDuration && (
                                <Chip
                                  label={`${config.customDuration} min`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                              <IconButton
                                size="small"
                                onClick={() => toggleExpand(serviceId)}
                                disabled={disabled}
                              >
                                <Iconify
                                  icon={
                                    isExpanded
                                      ? 'eva:chevron-up-fill'
                                      : 'eva:chevron-down-fill'
                                  }
                                />
                              </IconButton>
                            </>
                          )}
                        </Box>

                        <Collapse in={isEnabled && isExpanded}>
                          <Box
                            sx={{
                              pl: { xs: 0, md: 7 },
                              pr: { xs: 0, md: 2 },
                              pt: 2,
                              pb: 1,
                            }}
                          >
                            <Stack spacing={2}>
                              <TextField
                                label={translateKey("hardcoded.czas_trwania_minuty")}
                                type="number"
                                size="small"
                                value={config?.customDuration || ''}
                                onChange={(e) =>
                                  updateServiceConfig(serviceId, {
                                    customDuration: e.target.value
                                      ? parseInt(e.target.value)
                                      : undefined,
                                  })
                                }
                                placeholder={translateKey("hardcoded.defaultDurationMinutes", { duration: service.defaultDuration })}
                                disabled={disabled}
                                inputProps={{ min: 15, max: 180 }}
                                helperText={translateKey("hardcoded.pozostaw_puste_aby_uzyc_domyslnego_czasu")}
                              />

                              <TextField
                                label={translateKey("visit.notesDrawerButton")}
                                multiline
                                rows={2}
                                size="small"
                                value={config?.notes || ''}
                                onChange={(e) =>
                                  updateServiceConfig(serviceId, {
                                    notes: e.target.value,
                                  })
                                }
                                placeholder={translateKey("hardcoded.dodatkowe_informacje_o_usludze")}
                                disabled={disabled}
                              />
                            </Stack>
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            ),
          );
        })()}
      </Stack>
    </Card>
  );
}
